import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useAuthStore } from '@stores/authStore';
import { chatService } from '@services/chatService';
import type { CSChatMessage } from '../../types/api';

function generateSessionId(): string {
  return `cs-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

const CustomerServiceChatInner: React.FC = () => {
  const { isAuthenticated, user } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<CSChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [remainingRounds, setRemainingRounds] = useState<number | null>(null);
  const [limitReached, setLimitReached] = useState(false);

  const sessionIdRef = useRef(generateSessionId());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && !limitReached) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, limitReached]);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || isLoading || limitReached) return;

    const userMsg: CSChatMessage = { role: 'user', content: text, timestamp: Date.now() };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setError(null);
    setIsLoading(true);

    try {
      const res = await chatService.sendMessage(sessionIdRef.current, text);
      if (res.error) {
        setError(res.error);
        return;
      }
      const assistantMsg: CSChatMessage = {
        role: 'assistant',
        content: res.reply,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, assistantMsg]);

      if (res.remaining_rounds !== undefined) {
        setRemainingRounds(res.remaining_rounds);
      }
    } catch (err: any) {
      const data = err.response?.data;
      if (data?.limit_reached) {
        setLimitReached(true);
        setRemainingRounds(0);
        setError(data.error || '对话轮次已用完');
      } else {
        setError(data?.error || err.message || '发送失败，请重试');
      }
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, limitReached]);

  const handleClear = useCallback(async () => {
    try {
      await chatService.clearSession(sessionIdRef.current);
    } catch {
      // Ignore clear errors
    }
    sessionIdRef.current = generateSessionId();
    setMessages([]);
    setError(null);
    setRemainingRounds(null);
    setLimitReached(false);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const toggleOpen = () => setIsOpen((prev) => !prev);

  return (
    <>
      {/* Bubble button */}
      <button
        onClick={toggleOpen}
        className="fixed bottom-6 right-6 z-[1000] flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg transition-all hover:bg-blue-700 hover:scale-105 active:scale-95"
        aria-label={isOpen ? '关闭客服' : '打开客服'}
      >
        {isOpen ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        )}
      </button>

      {/* Chat panel */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-[1001] flex h-[500px] w-[360px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl max-[400px]:left-4 max-[400px]:right-4 max-[400px]:w-auto">
          {/* Header */}
          <div className="flex items-center justify-between bg-blue-600 px-4 py-3 text-white">
            <div className="flex items-center gap-2">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              <span className="font-medium">在线客服</span>
            </div>
            <button
              onClick={handleClear}
              className="rounded px-2 py-1 text-xs transition-colors hover:bg-blue-700"
              title="清除对话"
            >
              清除对话
            </button>
          </div>

          {/* Guest round indicator */}
          {!isAuthenticated && remainingRounds !== null && !limitReached && (
            <div
              className={`px-4 py-1.5 text-center text-xs ${
                remainingRounds <= 3 ? 'bg-orange-50 text-orange-600' : 'bg-gray-50 text-gray-500'
              }`}
            >
              剩余 {remainingRounds} 轮对话
            </div>
          )}

          {/* Limit reached banner */}
          {!isAuthenticated && limitReached && (
            <div className="bg-red-50 px-4 py-2 text-center text-xs text-red-600">
              对话轮次已用完，请
              <a href="/login" className="font-medium underline hover:text-red-700">
                登录
              </a>
              后继续使用
            </div>
          )}

          {/* Messages area */}
          <div className="flex-1 overflow-y-auto px-4 py-3">
            {messages.length === 0 && !isLoading && (
              <div className="flex h-full items-center justify-center">
                <div className="text-center text-gray-400">
                  <svg className="mx-auto mb-2 h-10 w-10 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                  <p className="text-sm">
                    {isAuthenticated && user ? `你好 ${user.username} , 有什么可以帮你的吗？` : '你好！有什么可以帮你的吗？'}
                  </p>
                </div>
              </div>
            )}

            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`mb-3 flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] whitespace-pre-wrap break-words rounded-2xl px-3 py-2 text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="mb-3 flex justify-start">
                <div className="rounded-2xl bg-gray-100 px-4 py-2 text-sm text-gray-400">
                  正在输入...
                </div>
              </div>
            )}

            {error && !limitReached && (
              <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-500">
                {error}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          {!limitReached && (
            <div className="border-t border-gray-100 px-4 py-3">
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="输入消息..."
                  disabled={isLoading}
                  className="flex-1 rounded-full border border-gray-200 px-4 py-2 text-sm outline-none transition-colors focus:border-blue-400 disabled:opacity-50"
                />
                <button
                  onClick={sendMessage}
                  disabled={isLoading || !input.trim()}
                  className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-white transition-colors hover:bg-blue-700 disabled:opacity-40"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export const CustomerServiceChat: React.FC = () => {
  return createPortal(<CustomerServiceChatInner />, document.body);
};
